import { Duration, Option, Schema } from 'effect';
import {
  defaultForStage,
  type StageDefaults,
} from '@listening/utils/stage-defaults';

/**
 * The one flag list. Every runtime feature flag, kill switch, experiment and
 * remote-config value is declared here once; the API resolver, the admin
 * editors, the client hooks and the PostHog definitions all read it.
 *
 * A flag is decided per request on the server, in this order (first value
 * wins): stage override, user override, PostHog (only in `posthog.stages`),
 * the stage's `default`. Overrides live in the `flag_override` table of each
 * stage's database, so a stage override is that stage's kill switch. Client
 * builds compile in their stage's `default` and render from it until
 * `Flags.Get` answers.
 *
 * @see docs/projects/active/unified-feature-flags.md
 */

export const FlagStage = Schema.Literals([
  'localstack',
  'development',
  'staging',
  'production',
]);
export type FlagStage = typeof FlagStage.Type;

/** The client app that reads a flag through `Flags.Get`. */
export const FlagApp = Schema.Literals(['web', 'mobile']);
export type FlagApp = typeof FlagApp.Type;

/**
 * When an app does not know the viewer's values yet: the first viewer after
 * the app launches with nothing saved for them, and a new viewer after a
 * sign-in or sign-out, until that viewer's `Flags.Get` answers.
 */
export const FlagMoment = Schema.Literals(['launch', 'sign-in', 'sign-out']);
export type FlagMoment = typeof FlagMoment.Type;

/**
 * What an app shows for a flag while it does not know the viewer's value.
 * Every case switches to the real value when it loads.
 *
 * - `default`: the build's compiled-in stage default.
 * - `signedOut`: the values this device last received while signed out, else
 *   the default. Stage-wide kill switches and rollouts keep applying through
 *   a sign-in; sign-out clears a user's values, so another user's overrides
 *   never carry over.
 * - `{ wait, on }`: at the listed moments, hold the UI that reads the flag
 *   for at most `wait` from when the viewer's request started, or until it
 *   fails; otherwise, and after that, behave like `signedOut`.
 */
export type FlagWhileUnknown =
  | 'default'
  | 'signedOut'
  | {
      readonly wait: Duration.Input;
      readonly on: ReadonlyArray<FlagMoment>;
    };

export type FlagDefinition<S extends Schema.Codec<unknown, unknown>> = {
  readonly description: string;
  readonly value: S;
  /**
   * The value per stage when no override or rollout decides, and whenever a
   * value fails to decode. Client builds start from it.
   */
  readonly default: StageDefaults<FlagStage, S['Type']>;
  /** Served by `Flags.Get` to these apps. Server-only flags leave it out. */
  readonly client?: {
    readonly apps: ReadonlyArray<FlagApp>;
    readonly whileUnknown: FlagWhileUnknown;
  };
  readonly posthog?: {
    /** The PostHog flag key. It is the bucketing hash input: never change it once live. */
    readonly key: string;
    /** Stages that ask PostHog. Every other stage skips straight to its default. */
    readonly stages: ReadonlyArray<FlagStage>;
    /**
     * Sends `$feature_flag_called` where the value reaches the user: server
     * reads for a server-only flag, `Flags.Get` from the flag's apps for a
     * client flag.
     */
    readonly trackExposure?: boolean;
  };
};

const defineFlags = <
  const D extends Record<
    string,
    FlagDefinition<Schema.Codec<unknown, unknown>>
  >,
>(
  definitions: D & { readonly [N in keyof D]: FlagDefinition<D[N]['value']> },
) => definitions;

export const DocConvertBackend = Schema.Literals(['convertapi', 'self']);
export type DocConvertBackend = typeof DocConvertBackend.Type;

export const Flags = defineFlags({
  kindle: {
    description:
      'Shows the Kindle integration entry points. On everywhere; the production stage override in the admin app is the kill switch. Builds without the Kindle code ignore it.',
    value: Schema.Boolean,
    default: { default: true },
    client: { apps: ['mobile'], whileUnknown: 'signedOut' },
  },
  startupAudioPrewarm: {
    description: 'Prewarms the audio player on mobile app start.',
    value: Schema.Boolean,
    default: { default: false },
    client: { apps: ['mobile'], whileUnknown: 'default' },
    posthog: {
      key: 'enable-startup-audio-prewarm',
      stages: ['development', 'staging', 'production'],
    },
  },
  debugTools: {
    description:
      'Shows the developer tools in production mobile builds. Development builds always show them.',
    value: Schema.Boolean,
    default: { default: false },
    client: { apps: ['mobile'], whileUnknown: 'default' },
    posthog: {
      key: 'enable-debug-tools',
      stages: ['development', 'staging', 'production'],
    },
  },
  googleDrive: {
    description:
      'Connect and import from Google Drive in the mobile apps. Production needs a user Allow.',
    value: Schema.Boolean,
    default: { default: true, production: false },
    client: { apps: ['mobile'], whileUnknown: 'signedOut' },
  },
  googleDriveWeb: {
    description: 'Connect and import from Google Drive on the web.',
    value: Schema.Boolean,
    default: { default: true },
    client: { apps: ['web'], whileUnknown: 'signedOut' },
  },
  dropbox: {
    description:
      'Connect and import from Dropbox in the mobile apps. Production needs a user Allow; mobile connections share the web pilot cap.',
    value: Schema.Boolean,
    default: { default: true, production: false },
    client: { apps: ['mobile'], whileUnknown: 'signedOut' },
  },
  dropboxWeb: {
    description:
      'Connect and import from Dropbox on the web. Production follows the dropbox-web-v1 rollout, capped at 50 completed connections; connected pilot users keep access and every new connection checks capacity.',
    value: Schema.Boolean,
    default: { default: true, production: false },
    client: { apps: ['web'], whileUnknown: 'signedOut' },
    posthog: {
      key: 'dropbox-web-v1',
      stages: ['production'],
      trackExposure: true,
    },
  },
  conversionV2: {
    description:
      'New uploads convert on pipeline v2 (Paddle OCR) and build a doc-pack. Documents already converted keep the pipeline they ran on.',
    value: Schema.Boolean,
    default: { default: false, development: true, staging: true },
    posthog: {
      key: 'conversion-pipeline-v2',
      stages: ['production'],
      trackExposure: true,
    },
  },
  docConvertBackend: {
    description:
      'Which converter turns a new non-PDF upload into a PDF first: "self" is LibreOffice (Calibre for ebooks) on our servers, "convertapi" is ConvertAPI. The other one is the fallback. Pinned when the upload is queued; documents already converted are unaffected.',
    value: DocConvertBackend,
    default: { default: 'self', production: 'convertapi' },
    posthog: {
      key: 'doc-convert-backend',
      stages: ['production'],
      trackExposure: true,
    },
  },
  readingLabels: {
    description:
      'Reading labels on web and mobile: the toggles that choose which labeled parts of a doc-pack document are read aloud, and the label overlay ("Show labels on page" on the web). Production follows the reading-labels rollout.',
    value: Schema.Boolean,
    default: { default: true, production: false },
    client: { apps: ['web', 'mobile'], whileUnknown: 'signedOut' },
    posthog: {
      key: 'reading-labels',
      stages: ['production'],
      trackExposure: true,
    },
  },
});

export type FlagName = keyof typeof Flags;
export type FlagValue<N extends FlagName> = (typeof Flags)[N]['value']['Type'];

/**
 * `Flags` seen through one shape per flag, so generic code indexed by a flag
 * name keeps that flag's value type.
 */
type FlagTable = {
  readonly [N in FlagName]: FlagDefinition<Schema.Codec<FlagValue<N>, unknown>>;
};
export const flagTable: FlagTable = Flags;

export const flagNames = Object.keys(Flags).filter(
  (name): name is FlagName => name in Flags,
);

export const FlagNameSchema = Schema.Literals(flagNames);

export const isFlagName = Schema.is(FlagNameSchema);

/**
 * The value schema of one flag, widened to the shape every flag shares so
 * generic code (decoding overrides, PostHog values, client payloads) can use
 * it. The result is still decoded with the flag's own schema.
 */
export const flagValueSchema = <N extends FlagName>(name: N) =>
  flagTable[name].value;

export const decodeFlagValue = <N extends FlagName>(name: N, raw: unknown) =>
  Schema.decodeUnknownOption(flagValueSchema(name))(raw);

export const encodeFlagValue = <N extends FlagName>(
  name: N,
  value: FlagValue<N>,
) => Schema.encodeSync(flagValueSchema(name))(value);

export const flagDefaultFor = <N extends FlagName>(name: N, stage: FlagStage) =>
  defaultForStage(flagTable[name].default, stage);

type FlagClient<N extends FlagName> = (typeof Flags)[N] extends {
  readonly client: infer C;
}
  ? C
  : never;

/** Flags `Flags.Get` serves to `app`. Reading any other flag there is a compile error. */
export type AppFlagName<A extends FlagApp> = {
  readonly [N in FlagName]: (typeof Flags)[N] extends {
    readonly client: {
      readonly apps: infer Apps extends ReadonlyArray<FlagApp>;
    };
  }
    ? A extends Apps[number]
      ? N
      : never
    : never;
}[FlagName];

/** An app's flags whose UI holds, at some moments, until the value loads. */
export type WaitingFlagName<A extends FlagApp> = {
  readonly [N in AppFlagName<A>]: FlagClient<N> extends {
    readonly whileUnknown: { readonly wait: unknown };
  }
    ? N
    : never;
}[AppFlagName<A>];

/** An app's flags that always render a value, real or fallback. */
export type InstantFlagName<A extends FlagApp> = Exclude<
  AppFlagName<A>,
  WaitingFlagName<A>
>;

export type ClientFlagName = AppFlagName<FlagApp>;

export const appFlagNames = <A extends FlagApp>(app: A) =>
  flagNames.filter(
    (name): name is AppFlagName<A> =>
      flagTable[name].client?.apps.includes(app) === true,
  );

/**
 * `Flags.Get` sends raw values keyed by flag name, and each reader decodes the
 * one it needs with that flag's schema. A strict struct would make an installed
 * app fail the whole response as soon as a flag it knows is removed or retyped.
 */
export const ClientFlagValues = Schema.Record(Schema.String, Schema.Unknown);
export type ClientFlagValues = typeof ClientFlagValues.Type;

/** What an app knows about a viewer's flags while rendering. */
export type ClientFlagView = {
  /** The current viewer's values: loaded, or saved on this device for them. */
  readonly viewer: ClientFlagValues | undefined;
  /** The values this device last received while signed out. */
  readonly signedOut: ClientFlagValues | undefined;
  readonly stage: FlagStage;
};

const sentValue = <N extends ClientFlagName>(
  values: ClientFlagValues | undefined,
  name: N,
) => decodeFlagValue(name, values?.[name]);

/** What the flag shows while the viewer's value is unknown and nothing holds. */
const unknownValue = <N extends ClientFlagName>(
  name: N,
  view: ClientFlagView,
) =>
  flagTable[name].client?.whileUnknown === 'default'
    ? flagDefaultFor(name, view.stage)
    : Option.getOrElse(sentValue(view.signedOut, name), () =>
        flagDefaultFor(name, view.stage),
      );

/** An instant flag: the viewer's value, else its `whileUnknown` fallback. */
export const readInstantClientFlag = <N extends InstantFlagName<FlagApp>>(
  name: N,
  view: ClientFlagView,
) =>
  Option.getOrElse(sentValue(view.viewer, name), () =>
    unknownValue(name, view),
  );

/**
 * A waiting flag: the viewer's value, else `None` while its UI should hold
 * (a listed moment, inside `wait` of the viewer's request, request not
 * failed), else its fallback.
 */
export const readWaitingClientFlag = <N extends WaitingFlagName<FlagApp>>(
  name: N,
  view: ClientFlagView & {
    readonly moment: FlagMoment;
    readonly waited: Duration.Duration;
    readonly failed: boolean;
  },
) => {
  const loaded = sentValue(view.viewer, name);
  if (Option.isSome(loaded)) return loaded;
  const policy = flagTable[name].client?.whileUnknown;
  const holding =
    policy !== undefined &&
    policy !== 'default' &&
    policy !== 'signedOut' &&
    policy.on.includes(view.moment) &&
    !view.failed &&
    Duration.isLessThan(view.waited, Duration.fromInputUnsafe(policy.wait));
  return holding
    ? Option.none<FlagValue<N>>()
    : Option.some(unknownValue(name, view));
};

export const FlagSource = Schema.Literals([
  'stage-override',
  'user-override',
  'posthog',
  'stage-default',
  'default',
  'pilot',
]);
export type FlagSource = typeof FlagSource.Type;
